import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Захват «на месте» (#1441): претендент занимает виджет, но не уносит его к
 * себе — виджет остаётся в обычном контейнере. Так звонок держит голос, а
 * окно переписки не пустеет. `isClaimed` и `releaseClaim` работают как у
 * обычного захвата.
 */
export const CLAIM_IN_PLACE = 'in-place';

const InlineChatHostContext = createContext({
  host: null,
  setHost: () => {},
  setClaimedHost: () => {},
  isClaimed: false,
  releaseClaim: () => {},
});

/**
 * Куда «приземлить» чат ассистента (#1330, пункт 7).
 *
 * ## Зачем понадобился контекст
 *
 * Виджет живёт на уровне `App` и НИКОГДА не размонтируется — на этом держится
 * непрерывность разговора: агент умеет уводить сайт на другую страницу
 * (`view_cars`, `set_filters`), и переписка обязана это пережить.
 *
 * Блок с инлайн-чатом при этом живёт внутри страницы. Прямой рендер виджета
 * внутрь блока означал бы второй экземпляр — а это, во-первых, размонтирование
 * вместе со страницей (первый же переход агента убивал бы переписку), и
 * во-вторых, драка за `browserControlWS`: синглтон, которому каждый чат-embed
 * перезаписывает командный набор. Ровно причина #859.
 *
 * Поэтому блок не рендерит виджет, а РЕГИСТРИРУЕТ свой контейнер. Виджет
 * остаётся одним и тем же и просто портируется внутрь него, пока контейнер на
 * странице есть. Ушли со страницы — контейнер снялся с регистрации, виджет
 * вернулся плавающим окном, с той же историей.
 */
/**
 * ## Два претендента на один виджет
 *
 * На странице чатов контейнеров ДВА: мокап переписки (всегда) и карточка
 * звонка (пока идёт разговор). Виджет один, и «последний зарегистрировался —
 * тот и хозяин» тут не годится: карточка звонка снялась бы с регистрации, и
 * виджет остался бы висеть неизвестно где вместо возврата в мокап.
 *
 * Поэтому у контейнера есть приоритет. Обычный (`setHost`) — фон страницы;
 * приоритетный (`setClaimedHost`) забирает виджет себе, пока он есть, и
 * отпускает обратно, как только снимается. Порядок регистрации при этом
 * значения не имеет.
 */
/**
 * ## Уступить виджет обратно (#1417)
 *
 * Пока идёт звонок, виджет занят голосом. Раньше чат при этом был
 * заблокирован до конца разговора; теперь покупатель, взявшийся за чат,
 * звонок просто завершает. Претендент передаёт вместе с узлом, КАК его
 * отпустить (`setClaimedHost(node, release)`), а мокап зовёт `releaseClaim()`
 * — сам он про звонок ничего не знает.
 */
export const InlineChatHostProvider = ({ children }) => {
  const [host, setHost] = useState(null);
  const [claimedHost, setClaimed] = useState(null);
  const releaseRef = useRef(null);
  const setClaimedHost = useCallback((node, release) => {
    releaseRef.current = node ? release ?? null : null;
    setClaimed(node);
  }, []);
  // Одноразово: pointerdown и следом click, клавиша и следом Enter — звонок
  // кладётся один раз.
  const releaseClaim = useCallback(() => {
    const release = releaseRef.current;
    releaseRef.current = null;
    release?.();
  }, []);
  const value = useMemo(
    () => ({
      host: claimedHost === CLAIM_IN_PLACE ? host : claimedHost ?? host,
      setHost,
      setClaimedHost,
      isClaimed: claimedHost != null,
      releaseClaim,
    }),
    [host, claimedHost, setClaimedHost, releaseClaim]
  );
  return (
    <InlineChatHostContext.Provider value={value}>{children}</InlineChatHostContext.Provider>
  );
};

InlineChatHostProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * `host` — элемент, в который надо портировать чат (или `null`).
 * `setHost` — годится прямо как `ref` на контейнер: React зовёт его с узлом
 * при монтировании и с `null` при размонтировании.
 * `setClaimedHost` — то же самое, но забирает виджет у обычного контейнера,
 * пока узел зарегистрирован (карточка звонка на время разговора). Второй
 * аргумент — как этот захват завершить.
 * `isClaimed` / `releaseClaim` — занят ли виджет претендентом и попросить его
 * уступить (мокап переписки, когда покупатель берётся за чат во время звонка).
 *
 * Переписка для мокапа инбокса едет НЕ здесь, а через `services/chatMirror`:
 * ответ агента приходит потоком, и контекст перерисовывал бы всё дерево на
 * каждый кусок текста.
 */
export const useInlineChatHost = () => useContext(InlineChatHostContext);
